import React, { memo, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { styled } from "@mui/styles";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
  ExpandMore,
  ChevronRight,
  ReplayRounded,
  CancelPresentation,
} from "@mui/icons-material";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import DownloadIcon from "@mui/icons-material/Download";

import { format } from "../../../utils/Date";
import useProjectStore from "../../ProjectCanvas/useProjectStore";
import { getComponentDisplayName } from "../ProjectLog/LogHelpers";
import { ComponentRegistry } from "../../ProjectCanvas/ComponentRegistry";
import { getDisplayStatus } from "./sessionIoViewModel";
import { renderStatus } from "./sessionStatusUI";
import SessionComponentBody from "./SessionComponentBody";

dayjs.extend(duration);

const SectionRoot = styled(Box)(({ theme, isSelected }) => ({
  borderRadius: "8px",
  border: `1px solid ${
    isSelected ? theme.palette.primary.main : theme.palette.divider
  }`,
  borderLeftWidth: isSelected ? "3px" : "1px",
  marginBottom: "12px",
  backgroundColor: theme.palette.background.paper,
  transition: "border-color 0.2s ease, background-color 0.6s ease",
  ...(isSelected && {
    backgroundColor: theme.palette.action.hover,
  }),
}));

const HeaderRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "10px 12px",
  cursor: "pointer",
  userSelect: "none",
  minWidth: 0,
  "&:hover .session-section-actions, &:focus-within .session-section-actions": {
    maxHeight: "48px",
    opacity: 1,
    pointerEvents: "auto",
    marginTop: "8px",
  },
});

const HeaderInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
});

const ActionsContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  flexWrap: "wrap",
  maxHeight: 0,
  opacity: 0,
  overflow: "hidden",
  pointerEvents: "none",
  marginTop: 0,
  transition:
    "max-height 0.15s ease, opacity 0.15s ease, margin-top 0.15s ease",
});

const NameText = styled(Typography)({
  fontSize: "14px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
});

const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
  flexShrink: 0,
}));

const BodyContainer = styled(Box)({
  padding: "0 16px 16px 16px",
});

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  padding: "4px",
  color: theme.palette.text.secondary,
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "& .MuiSvgIcon-root": { fontSize: "16px" },
}));

const TerminateIconButton = styled(IconButton)({
  padding: "4px",
  color: "#B00020",
  "&:hover": { backgroundColor: "rgba(176, 0, 32, 0.08)" },
  "& .MuiSvgIcon-root": { fontSize: "16px" },
});

function formatDuration(startAt, endAt) {
  if (!startAt || !endAt) return null;
  const start = dayjs(startAt);
  const end = dayjs(endAt);
  if (!start.isValid() || !end.isValid()) return null;
  let diff = end.diff(start);
  if (diff < 0) return null;
  if (diff < 1000) diff = 1000;
  const d = dayjs.duration(diff);
  let res = "";
  if (Number(d.asDays().toFixed()) > 0) res += `${d.asDays().toFixed()}d `;
  if (d.hours() > 0) res += `${d.hours()}h `;
  if (d.minutes() > 0) res += `${d.minutes()}m `;
  if (d.seconds() > 0) {
    if (res.trim() === "" && d.seconds() <= 1) res = "<1s";
    else res += `${d.seconds()}s `;
  }
  return res.trim() || null;
}

function isTriggerType(type) {
  const reg = ComponentRegistry.find((c) => c.name === type);
  return reg?.isTrigger || false;
}

function getComponentLogo(type) {
  const reg = ComponentRegistry.find((c) => c.name === type);
  return reg?.logo || reg?.sidebarLogo || null;
}

function SessionComponentSection({
  sectionKey,
  component,
  isExpanded,
  isSelected,
  isLatestRun,
  onToggleExpand,
  actions,
  actionLoading,
  bodyProps,
  registerRef,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    if (registerRef) registerRef(sectionKey, containerRef.current);
    return () => {
      if (registerRef) registerRef(sectionKey, null);
    };
  }, [sectionKey, registerRef]);

  // Stable handler so onToggleExpand can be the parent's untouched
  // toggleSection callback and React.memo on this component still
  // skips re-renders when other sections toggle.
  const handleToggle = useCallback(() => {
    onToggleExpand(sectionKey);
  }, [onToggleExpand, sectionKey]);

  const status = getDisplayStatus(component);
  const startedLabel = component.startAt
    ? format(component.startAt, useProjectStore.getState().timezone, "timeline")
    : null;
  const durationLabel = formatDuration(component.startAt, component.endAt);

  const isTrigger = isTriggerType(component.type);
  const logo = getComponentLogo(component.type);
  const showRerun =
    !isTrigger &&
    component.type !== "loop_through_list" &&
    component.status !== "FAIL" &&
    component.status !== "INPROGRESS";
  const showRetry = component.status === "FAIL";
  const showTerminate =
    component.status === "WAITING" || component.status === "INPROGRESS";
  const showRerunOnLatest = !isTrigger || component.type === "form";

  return (
    <SectionRoot
      ref={containerRef}
      isSelected={isSelected}
      className={isSelected ? "session-section-selected" : ""}
    >
      <HeaderRow
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <HeaderInfo>
          <Box
            aria-hidden="true"
            sx={{
              padding: "2px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
            }}
          >
            {isExpanded ? (
              <ExpandMore fontSize="small" />
            ) : (
              <ChevronRight fontSize="small" />
            )}
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            {renderStatus(status, { asChip: true, isLatestRun })}
          </Box>
          {logo && (
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ width: 16, height: 16, flexShrink: 0, opacity: 0.85 }}
            />
          )}
          <NameText>
            {component._nodeLabel || getComponentDisplayName(component.type)}
          </NameText>
          {component._nodeLabel && (
            <MetaText sx={{ ml: 0.5 }}>
              {getComponentDisplayName(component.type)}
            </MetaText>
          )}
          {startedLabel && (
            <MetaText sx={{ ml: 1 }}>
              {String(startedLabel).replace("\n", " ")}
            </MetaText>
          )}
          {durationLabel && <MetaText>· {durationLabel}</MetaText>}
        </HeaderInfo>

        <ActionsContainer
          className="session-section-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {showRerun && (
            <Tooltip title="Re-run this component">
              <span>
                <ActionIconButton
                  size="small"
                  onClick={() => actions.rerunComponent(component)}
                  disabled={actionLoading || !isLatestRun}
                >
                  <ReplayRounded />
                </ActionIconButton>
              </span>
            </Tooltip>
          )}
          {showRetry && (
            <Tooltip title="Retry this component">
              <span>
                <ActionIconButton
                  size="small"
                  onClick={() => actions.retryComponent(component)}
                  disabled={actionLoading || !isLatestRun}
                >
                  <ReplayRounded />
                </ActionIconButton>
              </span>
            </Tooltip>
          )}
          {showTerminate && (
            <Tooltip title="Terminate this component">
              <span>
                <TerminateIconButton
                  size="small"
                  onClick={() => actions.terminateComponent(component)}
                  disabled={actionLoading || !isLatestRun}
                >
                  <CancelPresentation />
                </TerminateIconButton>
              </span>
            </Tooltip>
          )}
          {showRerunOnLatest && (
            <Tooltip title="Re-run on latest version">
              <span>
                <ActionIconButton
                  size="small"
                  onClick={() => actions.openRerunOnLatestVersion(component)}
                  disabled={actionLoading || !isLatestRun}
                >
                  <MoveDownIcon />
                </ActionIconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Download component data as JSON">
            <span>
              <ActionIconButton
                size="small"
                onClick={() => actions.downloadComponentJson(component)}
              >
                <DownloadIcon />
              </ActionIconButton>
            </span>
          </Tooltip>
        </ActionsContainer>
      </HeaderRow>

      {isExpanded && (
        <BodyContainer>
          <SessionComponentBody component={component} {...bodyProps} />
        </BodyContainer>
      )}
    </SectionRoot>
  );
}

SessionComponentSection.propTypes = {
  sectionKey: PropTypes.string.isRequired,
  component: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  isLatestRun: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  actionLoading: PropTypes.bool,
  bodyProps: PropTypes.object.isRequired,
  registerRef: PropTypes.func.isRequired,
};

// React.memo + the stable props plumbed in from SessionDocumentPanel
// (memoized bodyProps, memoized actions from useSessionComponentActions,
// stable toggleSection callback) so a search keystroke that re-renders
// the panel does not reconcile every mounted section. Only the JsonPanels
// actually subscribed to SearchContext re-run.
export default memo(SessionComponentSection);
